import {Observable} from 'rxjs';

/**
 * ใช้กับ pdftransform แบบ async: เอกสารที่ backend ตอบ converting = true (ยังแปลงเป็น PDF ไม่เสร็จ)
 * จะถูก poll ซ้ำเฉพาะตัวที่ค้าง แล้วเรียก onReady ทีละไฟล์เมื่อแปลงเสร็จ
 * ทำให้ผู้ใช้เห็นเอกสารที่พร้อมก่อนได้ทันทีโดยไม่ต้องรอแปลงครบทุกไฟล์
 *
 * ลดภาระ service/Alfresco: ช่วง poll ค่อยๆ ยืดออก (backoff), ไม่ยิงขณะ tab ถูกซ่อน,
 * และเลิก poll เมื่อเกินเวลาสูงสุด. component อื่นที่ต้องการผลของเอกสารชุดเดียวกัน (เช่น thumbnail)
 * ใช้ subscribeReady() รับผลจาก watcher นี้แทนการ poll ซ้ำเอง
 */
export class PdfRenditionWatcher {
   private generation = 0;
   private timer: any;
   /** เอกสารที่แปลงเสร็จหลังโหลดหน้า: ข้อมูลหน้าที่ component สร้างไว้ตอนโหลดยังว่าง ต้องโหลดใหม่ก่อนแสดง */
   private staleIndexes = new Set<number>();
   /** เอกสารที่รู้แล้วว่าพร้อม (docId -> ผล pdftransform) ให้ผู้ subscribe ทีหลังได้รับด้วย */
   private readyDocs = new Map<string, any>();
   private listeners = new Set<(doc: any) => void>();
   /** docId ที่ watch รอบล่าสุดยังรอแปลงอยู่ (ใช้แจ้ง backend ตอนออกจากหน้า) */
   private pending: { docId: string; index: number }[] = [];

   constructor(private initialIntervalMs = 3000,
               private maxIntervalMs = 20000,
               private maxDurationMs = 30 * 60 * 1000) {
   }

   static isConverting(doc: any): boolean {
      return !!doc && doc.converting === true;
   }

   /**
    * @param data ผลลัพธ์จาก pdftransform (เรียงตาม docIds ที่ส่งไป)
    * @param fetch เรียก pdftransform (async) ซ้ำด้วย docId ที่ยังค้าง
    * @param onReady เรียกเมื่อเอกสารตำแหน่ง index แปลงเสร็จ (doc.converting = false)
    * @param currentIndex index ของเอกสารที่ผู้ใช้เปิดอยู่: ส่งเป็นลำดับแรกใน request ให้ backend แปลงไฟล์นี้ก่อน
    */
   watch(data: any[], fetch: (docIds: string[]) => Observable<any>, onReady: (doc: any, index: number) => void,
         currentIndex?: () => number) {
      this.stop();
      this.staleIndexes.clear();
      (data || []).filter(doc => doc?.docId && !PdfRenditionWatcher.isConverting(doc)).forEach(doc => this.markReady(doc));
      const generation = this.generation;
      const pending = (data || [])
         .map((doc, index) => ({docId: doc?.docId, index}))
         .filter(item => PdfRenditionWatcher.isConverting(data[item.index]));
      this.pending = pending;
      if (pending.length === 0) {
         return;
      }
      const startedAt = Date.now();
      let delay = this.initialIntervalMs;
      const schedule = () => {
         if (generation !== this.generation) {
            return;
         }
         if (Date.now() - startedAt > this.maxDurationMs) {
            return; // เลิก poll: ผู้ใช้เปิดเอกสารใหม่ (refresh/คลิกเอกสาร) จะเริ่ม watch ใหม่เอง
         }
         this.timer = setTimeout(tick, delay);
         delay = Math.min(Math.round(delay * 1.5), this.maxIntervalMs);
      };
      const tick = () => {
         if (generation !== this.generation) {
            return;
         }
         if (typeof document !== 'undefined' && document.hidden) {
            schedule(); // tab ถูกซ่อน: ไม่ยิง request แค่รอรอบถัดไป
            return;
         }
         const current = currentIndex?.();
         const ordered = [...pending].sort((a, b) => Number(b.index === current) - Number(a.index === current));
         fetch(ordered.map(item => item.docId)).subscribe((result: any[]) => {
            if (generation !== this.generation) {
               return;
            }
            for (const doc of result || []) {
               const k = pending.findIndex(item => item.docId === doc?.docId);
               if (k >= 0 && !PdfRenditionWatcher.isConverting(doc)) {
                  const item = pending.splice(k, 1)[0];
                  this.staleIndexes.add(item.index);
                  this.markReady(doc);
                  onReady(doc, item.index);
               }
            }
            if (pending.length > 0) {
               schedule();
            }
         }, () => schedule());
      };
      schedule();
   }

   /** รับแจ้งเมื่อเอกสารพร้อม (รวมเอกสารที่พร้อมอยู่แล้ว) คืนฟังก์ชันสำหรับเลิกรับ */
   subscribeReady(listener: (doc: any) => void): () => void {
      this.listeners.add(listener);
      this.readyDocs.forEach(doc => listener(doc));
      return () => this.listeners.delete(listener);
   }

   isStale(index: number): boolean {
      return this.staleIndexes.has(index);
   }

   stop() {
      this.generation++;
      clearTimeout(this.timer);
   }

   /**
    * ออกจากหน้า/cancel: หยุด poll และส่ง docId ที่ยังรอแปลงให้ send (เรียก pdftransform/release)
    * backend จะเอาออกจากคิวถ้ายังไม่เริ่มแปลงและไม่มีผู้ใช้อื่นรออยู่
    */
   release(send: (docIds: string[]) => void) {
      this.stop();
      const docIds = this.pending.map(item => item.docId).filter(Boolean);
      this.pending = [];
      if (docIds.length > 0) {
         send(docIds);
      }
   }

   private markReady(doc: any) {
      this.readyDocs.set(doc.docId, doc);
      this.listeners.forEach(listener => listener(doc));
   }
}
